import React, { Fragment, useEffect, useState } from 'react';
import BubbleButton from './BubbleButton';
import BubbleOverlay from './BubbleOverlay';

export interface BubbleFormActions {
  goBack: () => void;
  goForward: () => void;
  goStart: () => void;
}

export interface BubbleFormPage<T> {
  key: string;
  render: (actions: BubbleFormActions, resources?: T) => React.ReactNode;
}

interface BubbleFormProps<T> {
  pages: Array<BubbleFormPage<T>>;
  resources?: T;
  startAtPage?: number;
}

export interface FormPageComponentHelpfulProps {
  title?: string;
  subtitle?: string;
  pageIndex?: number;

  backTitle?: string;
  forwardTitle?: string;

  onClose: () => void;
  onGoBack?: () => void;
  onGoForward?: () => void;
}

export default function BubbleForm<T>(props: BubbleFormProps<T>) {
  const firstPage = 0;

  const [currentPage, setCurrentPage] = useState(-1);

  useEffect(() => {
    setCurrentPage(props.startAtPage || firstPage);
  }, [props.startAtPage]);

  function goBack() {
    setCurrentPage(Math.max(currentPage - 1, firstPage));
  }

  function goForward() {
    setCurrentPage(Math.min(currentPage + 1, props.pages.length - 1));
  }

  function goStart() {
    setCurrentPage(props.startAtPage || firstPage);
  }

  return (
    <div className="bubble-form">
      {props.pages.map((page, indx) => (
        <div key={page.key} className="form-page">
          {currentPage !== -1 &&
            currentPage === indx &&
            page.render({ goBack, goForward, goStart }, props.resources)}
        </div>
      ))}
    </div>
  );
}

interface FormActionProps {
  onGoBack?: () => void;
  onGoForward?: () => void;
  backTitle?: string;
  forwardTitle?: string;
  backDisabled?: boolean;
  forwardDisabled?: boolean;
  isBusy?: boolean;
}

export function BubbleFormActions(props: FormActionProps) {
  return (
    <div className="flex items-center">
      {props.onGoBack && (
        <BubbleOverlay loading={props.isBusy}>
          <BubbleButton
            variant="navy"
            inverse
            onClick={props.onGoBack}
            disabled={props.backDisabled}
          >
            {props.backTitle || 'Go Back'}
          </BubbleButton>
        </BubbleOverlay>
      )}
      {props.onGoForward && (
        <BubbleOverlay loading={props.isBusy}>
          <BubbleButton disabled={props.forwardDisabled} variant="navy" onClick={props.onGoForward}>
            {props.forwardTitle || 'Next'}
          </BubbleButton>
        </BubbleOverlay>
      )}
    </div>
  );
}

interface FormHeaderProps {
  title: string;
  subtitle: string;
  pageIndex?: number;
  onClose: () => void;
}

export function BubbleFormHeader(props: FormHeaderProps) {
  return (
    <div className="flex items-start mb-5">
      <div>
        <div className="flex items-center">
          <h1
            className="text-lg text-slate-dark font-semibold"
            dangerouslySetInnerHTML={{ __html: props.title }}
          />
        </div>
        <h2 className="text-2xl text-navy">
          {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}
          {props.subtitle}
        </h2>
      </div>
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        inverse
        onClick={props.onClose}
        icon="xmark-large"
        iconSize="xl"
      />
    </div>
  );
}

interface BubbleFormFooterItem {
  label: string;
  value: string | React.ReactNode | undefined;
}

interface FormFooterProps {
  items: BubbleFormFooterItem[];
}

export function BubbleFormFooter(props: React.HTMLAttributes<HTMLDivElement> & FormFooterProps) {
  return (
    <div className={props.className}>
      {props.items.map((item, index) => (
        <Fragment key={index}>
          {item.value !== undefined && (
            <div>
              <span>{item.label}: </span>
              <span className="font-semibold">{item.value}</span>
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
}
